import { ChatMessageType, type ChatConversationDto, type ChatMessageDto } from "../../contracts";
import { getSimulationHeadingData, getSimulationPlanData } from "../../hooks/useCurrentPlan";
import {
    isCommercialSimulatorSubBrandToolRequest,
    isCommercialSimulatorToolRequest,
    type CommercialSimulatorSubBrandToolRequest,
    type CommercialSimulatorToolRequest
} from "../../models/toolRequestModels";
import { CommercialHistoryItemStatusTypes } from "../../pages/workspace/components/types/commercialHistoryTypes";
import { formatMonthDate } from "../../utils/dateValueConversion";

export type CommercialHistoryItemStatusType = typeof CommercialHistoryItemStatusTypes[keyof typeof CommercialHistoryItemStatusTypes];

type CommercialHistoryItemViewModel = {
    title: string;
    contextLine: string;
    status: CommercialHistoryItemStatusType;
    metaText: string;
};

const getLastCommercialSimulatorToolRequest = (
    messages: ChatMessageDto[]
): CommercialSimulatorToolRequest | CommercialSimulatorSubBrandToolRequest | undefined => {
    for (let index = messages.length - 1; index >= 0; index--) {
        const message = messages[index];

        if (message.type !== ChatMessageType.ToolRequest) {
            continue;
        }

        const payload = message.payload;

        if (isCommercialSimulatorToolRequest(payload) || isCommercialSimulatorSubBrandToolRequest(payload)) {
            return payload;
        }
    }

    return undefined;
};

const getConversationTitle = (conversation: ChatConversationDto): string =>
    conversation.name?.trim() || `Plan ${conversation.id}`;

const getLastUserMessageSummary = (conversation: ChatConversationDto): string => {
    if (conversation.summary) {
        return conversation.summary;
    }

    const userMessages = conversation.messages.filter(message => message.type === ChatMessageType.User);
    const lastUserMessage = userMessages.length > 0 ? userMessages[userMessages.length - 1] : undefined;
    const text = lastUserMessage?.text?.trim() ?? "";

    if (!text) {
        return "No user message yet.";
    }

    return text.length > 88 ? `${text.slice(0, 88).trimEnd()}...` : text;
};

const getCommercialHistoryItemTitle = (conversation: ChatConversationDto): string => {
    const messages = conversation.messages ?? [];
    const toolRequest = getLastCommercialSimulatorToolRequest(messages);

    if (!toolRequest) {
        return getConversationTitle(conversation);
    }

    const headingData = getSimulationHeadingData(messages, toolRequest);

    if (!headingData?.objective || !headingData.retailers) {
        return getConversationTitle(conversation);
    }

    return `${headingData.objective} at ${headingData.retailers}`;
};

const getCommercialHistoryStatus = (conversation: ChatConversationDto): CommercialHistoryItemStatusType => {
    const { messages = [] } = conversation;

    const hasCompletedJob = messages.some(message => message.type === ChatMessageType.AgentResponse);
    if (hasCompletedJob) {
        return CommercialHistoryItemStatusTypes.SIMULATION_COMPLETE;
    }

    const hasActiveSimulationJob = messages.some(message => message.type === ChatMessageType.AgentRequest);
    if (hasActiveSimulationJob) {
        return CommercialHistoryItemStatusTypes.SIMULATION_RUNNING;
    }

    return CommercialHistoryItemStatusTypes.OPEN_CONVERSATION;
};

const getCommercialHistoryItemContextLine = (conversation: ChatConversationDto): string => {
    const messages = conversation.messages ?? [];
    const toolRequest = getLastCommercialSimulatorToolRequest(messages);

    if (!toolRequest) {
        return getLastUserMessageSummary(conversation);
    }

    const headingData = getSimulationHeadingData(messages, toolRequest);
    const planData = getSimulationPlanData(toolRequest);

    if (!headingData || !planData) {
        return getLastUserMessageSummary(conversation);
    }

    const selection = headingData.skuName ?? headingData.subBrandName?.replace(/\s+/g, " ").trim();
    const parts = [planData.planningLevel, selection, headingData.retailers].filter((part): part is string => Boolean(part?.trim()));

    if (parts.length === 0) {
        return getLastUserMessageSummary(conversation);
    }

    return parts.join(" • ");
};

export const getCommercialHistoryMetaText = (
    conversation: ChatConversationDto,
    status: CommercialHistoryItemStatusType
): string => {
    if (status === CommercialHistoryItemStatusTypes.SIMULATION_RUNNING) {
        return "Running";
    }

    return formatMonthDate(conversation.last_message_at ?? conversation.created_at);
};

const getCommercialHistoryItemViewModel = (
    conversation: ChatConversationDto
): CommercialHistoryItemViewModel => {
    const title = getCommercialHistoryItemTitle(conversation);
    const contextLine = getCommercialHistoryItemContextLine(conversation);
    const status = getCommercialHistoryStatus(conversation);
    const metaText = getCommercialHistoryMetaText(conversation, status);

    return {
        title,
        contextLine,
        status,
        metaText
    };
};

export const filterConversations = (
    conversations: ChatConversationDto[],
    searchTerm: string
): ChatConversationDto[] => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
        return conversations;
    }

    return conversations.filter(conversation => {
        const title = getCommercialHistoryItemTitle(conversation).toLowerCase();
        const contextLine = getCommercialHistoryItemContextLine(conversation).toLowerCase();

        return title.includes(normalizedSearchTerm) || contextLine.includes(normalizedSearchTerm);
    });
};

const useCommercialHistoryItem = (
    conversation: ChatConversationDto
): CommercialHistoryItemViewModel => {
    return getCommercialHistoryItemViewModel(conversation);
};

export default useCommercialHistoryItem;
